import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { FileArchive, Store as StoreIcon, UserRound } from 'lucide-react';
import { ApiError } from '@/shared/api/client';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { CopyableField } from '@/shared/components/copyable-field';
import { SecretInput } from '@/shared/components/secret-input';
import { cn } from '@/shared/lib/cn';
import { Wizard, type WizardStep } from '@/shared/components/wizard';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import type { AdminClient, AdminStore } from '@/shared/types/client';
import { cloneClient, createStores, fetchPassbookStores, type ClientCloneResult } from '../api/use-clients';
import { isValidJson } from '../model/regra-cartao';
import { RegraCartaoEditor } from './regra-cartao-editor';
import { ClientCloneAdvancedFields } from './client-clone-advanced-fields';
import { ClientCloneStoresStep } from './client-clone-stores-step';

/** Integrações de PDV/ERP conhecidas hoje (dado real: webautomacao é de
 * longe a mais comum, seguida de colibri/goomer/totvs). "Outro" no select
 * libera um campo de texto livre (normalizado pra minúsculo no envio). */
export const PARTNER_OPTIONS = ['webautomacao', 'colibri', 'goomer', 'totvs'] as const;

/** Sentinela pra "nenhum(a)" em `partnerName`/`redeemType`. Radix não aceita
 * `<SelectItem value="">` (string vazia é reservada internamente pro estado
 * sem-seleção/placeholder), então em vez de ficar convertendo ida-e-volta
 * dentro de cada Select, o próprio form guarda esse valor — a única
 * conversão pra "" (o que a api espera) acontece uma vez só, ao montar o
 * FormData em `submitIdentityStep`. */
export const NONE_VALUE = '__none__';

const schema = z.object({
  cnpj: z.string().trim().min(11, 'Informe um CNPJ válido'),
  name: z.string().trim().min(1, 'Informe o nome do cliente'),
  email: z.string().trim().min(1, 'Informe o e-mail').email('E-mail inválido'),
  slug: z
    .string()
    .trim()
    .min(1, 'Informe o slug')
    .regex(/^[a-z0-9-]+$/, 'Use apenas letras minúsculas, números e hífen'),
  backgroundColor: z.string().trim().min(1, 'Informe a cor de fundo'),
  labelColor: z.string().trim().min(1, 'Informe a cor dos rótulos'),
  foregroundColor: z.string().trim().min(1, 'Informe a cor de destaque'),
  organizationName: z.string().trim().min(1, 'Informe o nome exibido no cartão'),
  // Campos avançados — todos opcionais, com fallback pro valor da origem
  // no backend quando não tocados (ver api/controllers/client.js exports.clone).
  licenca: z.string().trim().optional(),
  regraCartao: z.string().optional(),
  hasGWallet: z.boolean(),
  hasFixeGWallet: z.boolean(),
  expireDays: z
    .string()
    .trim()
    .optional()
    .refine((v) => !v || /^\d+$/.test(v), 'Informe um número inteiro'),
  hasExpire: z.boolean(),
  hasMultiStore: z.boolean(),
  maxInputValidation: z
    .string()
    .trim()
    .optional()
    .refine((v) => !v || /^\d+$/.test(v), 'Informe um número inteiro'),
  // Só relevantes no modo "selos" (hasPoints e hasCashback ambos false).
  hasExpireStamps: z.boolean(),
  defaultBalance: z
    .string()
    .trim()
    .optional()
    .refine((v) => !v || /^\d+$/.test(v), 'Informe um número inteiro'),
  partner: z.boolean(),
  partnerName: z.string(),
  partnerNameOther: z.string().trim().optional(),
  redeemType: z.string(),
});

export type FormValues = z.infer<typeof schema>;

const EMPTY: FormValues = {
  cnpj: '',
  name: '',
  email: '',
  slug: '',
  backgroundColor: 'rgb(255, 255, 255)',
  labelColor: 'rgb(0, 0, 0)',
  foregroundColor: 'rgb(0, 0, 0)',
  organizationName: '',
  licenca: '',
  regraCartao: '',
  hasGWallet: false,
  hasFixeGWallet: false,
  expireDays: '',
  hasExpire: false,
  hasMultiStore: false,
  maxInputValidation: '',
  hasExpireStamps: false,
  defaultBalance: '',
  partner: false,
  partnerName: NONE_VALUE,
  partnerNameOther: '',
  redeemType: NONE_VALUE,
};

/**
 * Pré-preenche o form com os dados do passbook de origem — cores, e agora
 * também todos os campos avançados do programa. O operador só edita o que
 * realmente precisa mudar; tudo que não for tocado vai clonado como está
 * (o backend já garante isso via deep clone, mesmo sem reenviar o campo).
 */
function defaultsFor(client: AdminClient | null): FormValues {
  const passbook = client?.passbooks?.[0];
  const sourcePartnerName = passbook?.partnerName ?? '';
  const isKnownPartner = (PARTNER_OPTIONS as readonly string[]).includes(sourcePartnerName);
  return {
    ...EMPTY,
    backgroundColor: passbook?.backgroundColor ?? EMPTY.backgroundColor,
    labelColor: passbook?.labelColor ?? EMPTY.labelColor,
    foregroundColor: passbook?.foregroundColor ?? EMPTY.foregroundColor,
    regraCartao: JSON.stringify(passbook?.regraCartao ?? {}, null, 2),
    hasGWallet: passbook?.hasGWallet ?? false,
    hasFixeGWallet: passbook?.hasFixeGWallet ?? false,
    expireDays: passbook?.expireDays != null ? String(passbook.expireDays) : '',
    hasExpire: passbook?.hasExpire ?? false,
    hasMultiStore: passbook?.hasMultiStore ?? false,
    maxInputValidation: passbook?.maxInputValidation != null ? String(passbook.maxInputValidation) : '',
    hasExpireStamps: passbook?.hasExpireStamps ?? false,
    defaultBalance: passbook?.defaultBalance != null ? String(passbook.defaultBalance) : '',
    partner: passbook?.partner ?? false,
    partnerName: sourcePartnerName && !isKnownPartner ? 'outro' : sourcePartnerName || NONE_VALUE,
    partnerNameOther: sourcePartnerName && !isKnownPartner ? sourcePartnerName : '',
    redeemType: passbook?.redeemType || NONE_VALUE,
  };
}

interface ClientCloneDialogProps {
  /** `null` mantém o dialog fechado — evita depender de outro estado para saber o alvo. */
  client: AdminClient | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Disparado assim que o clone é criado (ainda com o dialog aberto, no step de lojas). */
  onCloned: () => void;
}

/**
 * Clona um client existente (+ passbook embutido + user de login) e, num
 * 2º step, revisa/cria as lojas (`stores`) do passbook novo. MVP: só
 * funciona para clients com exatamente 1 passbook embutido —
 * `client-card.tsx` já desabilita a ação nesse caso.
 *
 * Os dois steps disparam requests HTTP separadas: o 1º (identidade +
 * campos avançados + zip) roda de verdade dentro do `validate()` do step
 * — se falhar, o wizard não avança; se der certo, o step de lojas só
 * aparece porque o client já foi criado. O 2º (`createStores`) roda no
 * `onFinish`, só na etapa final.
 */
export function ClientCloneDialog({ client, open, onOpenChange, onCloned }: ClientCloneDialogProps) {
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [result, setResult] = useState<ClientCloneResult | null>(null);
  const [finished, setFinished] = useState(false);
  const [storesCreated, setStoresCreated] = useState(0);
  const [stores, setStores] = useState<AdminStore[]>([]);
  const [touchedStoreIndexes, setTouchedStoreIndexes] = useState<Set<number>>(new Set());
  const [storesLoading, setStoresLoading] = useState(false);
  const [submittingFinish, setSubmittingFinish] = useState(false);
  const [sourceStores, setSourceStores] = useState<AdminStore[]>([]);

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY });

  const sourcePassbook = client?.passbooks?.[0];
  const passbookCount = client?.passbooks?.length ?? 0;
  const canClone = passbookCount === 1;

  // Toda vez que o dialog abre para um client, repõe o form a partir do
  // passbook de origem e busca as lojas dele (pra já estarem prontas
  // quando o step 2 aparecer).
  useEffect(() => {
    if (!open) return;
    form.reset(defaultsFor(client));
    setResult(null);
    setFinished(false);
    setStoresCreated(0);
    setStores([]);
    setTouchedStoreIndexes(new Set());
    setSourceStores([]);

    const sourcePassbookId = client?.passbooks?.[0]?._id;
    if (!sourcePassbookId) return;
    setStoresLoading(true);
    fetchPassbookStores(sourcePassbookId)
      .then((data) => setSourceStores(data))
      .catch(() => setSourceStores([]))
      .finally(() => setStoresLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, client]);

  const reset = () => {
    form.reset(EMPTY);
    setZipFile(null);
    setResult(null);
    setFinished(false);
    setStoresCreated(0);
    setStores([]);
    setTouchedStoreIndexes(new Set());
    setSourceStores([]);
  };

  // Step 1: valida o form inteiro, valida o JSON de regraCartao à parte
  // (zod só garante que é string), e — se tudo certo — chama de verdade o
  // POST /client/:id/clone. Só avança pro step 2 se isso funcionar.
  const submitIdentityStep = async (): Promise<boolean> => {
    if (!client) return false;

    const valid = await form.trigger();
    if (!valid) return false;

    const values = form.getValues();
    if (values.regraCartao && !isValidJson(values.regraCartao)) {
      form.setError('regraCartao', { message: 'JSON inválido' });
      toast.error('Corrija o JSON de regraCartao antes de continuar.');
      return false;
    }
    if (!zipFile) {
      toast.error('Selecione o arquivo .zip com as imagens do passbook.');
      return false;
    }

    const resolvedPartnerName =
      values.partnerName === 'outro'
        ? (values.partnerNameOther ?? '').trim().toLowerCase()
        : values.partnerName === NONE_VALUE
          ? ''
          : values.partnerName;

    const formData = new FormData();
    formData.append('cnpj', values.cnpj.trim());
    formData.append('name', values.name.trim());
    formData.append('email', values.email.trim());
    formData.append('slug', values.slug.trim());
    formData.append('backgroundColor', values.backgroundColor.trim());
    formData.append('labelColor', values.labelColor.trim());
    formData.append('foregroundColor', values.foregroundColor.trim());
    formData.append('organizationName', values.organizationName.trim());
    formData.append('regraCartao', values.regraCartao?.trim() ?? '');
    formData.append('hasGWallet', String(values.hasGWallet));
    formData.append('hasFixeGWallet', String(values.hasFixeGWallet));
    formData.append('expireDays', values.expireDays?.trim() ?? '');
    formData.append('hasExpire', String(values.hasExpire));
    formData.append('hasExpireStamps', String(values.hasExpireStamps));
    formData.append('defaultBalance', values.defaultBalance?.trim() ?? '');
    formData.append('hasMultiStore', String(values.hasMultiStore));
    formData.append('maxInputValidation', values.maxInputValidation?.trim() ?? '');
    formData.append('partner', String(values.partner));
    formData.append('partnerName', resolvedPartnerName);
    formData.append('redeemType', values.redeemType === NONE_VALUE ? '' : values.redeemType);
    formData.append('zip', zipFile);

    try {
      const cloned = await cloneClient(client._id, formData);
      setResult(cloned);

      // Defaults do step de lojas, calculados agora que já temos o
      // organizationName final e a licença (se houver). Loja única =
      // renomeia pro novo organizationName; várias lojas mantêm o nome
      // original de cada uma.
      const finalOrgName = values.organizationName.trim();
      const licencaValue = values.licenca?.trim();
      const licencaPatch = licencaValue ? { branchId: licencaValue, storeCod: licencaValue } : {};
      const initialStores =
        sourceStores.length === 1
          ? [{ ...sourceStores[0], _id: undefined, id_passbook: undefined, storeName: finalOrgName, branchName: finalOrgName, ...licencaPatch }]
          : sourceStores.map((s) => ({ ...s, _id: undefined, id_passbook: undefined, ...licencaPatch }));
      setStores(initialStores);
      setTouchedStoreIndexes(new Set());

      toast.success('Cliente clonado — revise as lojas antes de concluir.');
      onCloned(); // client já existe no banco — recarrega a lista agora, não só ao fechar
      return true;
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? ((err.data as { error?: string } | undefined)?.error ?? err.message)
          : 'Falha ao clonar cliente.';
      toast.error(msg);
      return false;
    }
  };

  const handleFinish = async () => {
    if (!result) return;
    setSubmittingFinish(true);
    try {
      const meaningfulStores = stores.filter((s) => (s.storeName ?? '').trim() || (s.branchName ?? '').trim());
      const res = await createStores(result.passbook._id, meaningfulStores);
      setStoresCreated(res.created);
      setFinished(true);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? ((err.data as { error?: string } | undefined)?.error ?? err.message)
          : 'Falha ao cadastrar as lojas.';
      toast.error(msg);
    } finally {
      setSubmittingFinish(false);
    }
  };

  const steps: WizardStep[] = [
    {
      id: 'identity',
      title: 'Identidade',
      icon: UserRound,
      validate: submitIdentityStep,
      content: (
        <div className="grid gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="CNPJ" error={form.formState.errors.cnpj?.message}>
              <Input placeholder="Somente números" {...form.register('cnpj')} />
            </Field>
            <Field label="Slug do Fixe.Club" error={form.formState.errors.slug?.message}>
              <Input placeholder="novo-cliente" {...form.register('slug')} />
            </Field>
          </div>
          <Field label="Nome do cliente" error={form.formState.errors.name?.message}>
            <Input placeholder="Nome exibido no painel" {...form.register('name')} />
          </Field>
          <Field label="E-mail de acesso" error={form.formState.errors.email?.message}>
            <Input type="email" placeholder="contato@cliente.com.br" {...form.register('email')} />
          </Field>
          <Field
            label="Nome no cartão (organizationName)"
            error={form.formState.errors.organizationName?.message}
          >
            <Input placeholder="Nome exibido no passbook" {...form.register('organizationName')} />
          </Field>
          <p className="text-xs text-muted-foreground">
            Cores pré-preenchidas com as do cliente de origem — só troque se quiser um visual diferente.
          </p>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Cor de fundo" error={form.formState.errors.backgroundColor?.message}>
              <Input placeholder="rgb(255, 255, 255)" {...form.register('backgroundColor')} />
            </Field>
            <Field label="Cor dos rótulos" error={form.formState.errors.labelColor?.message}>
              <Input placeholder="rgb(0, 0, 0)" {...form.register('labelColor')} />
            </Field>
            <Field label="Cor de destaque" error={form.formState.errors.foregroundColor?.message}>
              <Input placeholder="rgb(0, 0, 0)" {...form.register('foregroundColor')} />
            </Field>
          </div>

          <ClientCloneAdvancedFields form={form} sourcePassbook={sourcePassbook} />

          <RegraCartaoEditor
            value={form.watch('regraCartao') ?? ''}
            onChange={(v) => form.setValue('regraCartao', v)}
            error={form.formState.errors.regraCartao?.message}
          />

          <div className="space-y-2">
            <Label htmlFor="clonePassbookZip">Imagens do passbook (.zip)</Label>
            <label
              htmlFor="clonePassbookZip"
              className="group flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-[hsl(var(--glass-bg)/0.4)] py-6 text-center transition-colors hover:border-primary/45 hover:bg-primary/[0.06]"
            >
              <FileArchive className="size-5 text-muted-foreground group-hover:text-primary" />
              <span className="text-sm text-muted-foreground">
                {zipFile ? zipFile.name : 'Selecione o .zip com icon.png, logo.png, strip_*.png…'}
              </span>
            </label>
            <Input
              id="clonePassbookZip"
              type="file"
              accept=".zip"
              className="sr-only"
              onChange={(e) => setZipFile(e.target.files?.[0] ?? null)}
            />
          </div>
        </div>
      ),
    },
    {
      id: 'stores',
      title: 'Lojas',
      icon: StoreIcon,
      content: (
        <ClientCloneStoresStep
          stores={stores}
          onChange={setStores}
          touchedIndexes={touchedStoreIndexes}
          onTouchedChange={setTouchedStoreIndexes}
          loading={storesLoading}
        />
      ),
    },
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent
        className={cn(
          'max-w-lg',
          // O Wizard precisa que DialogContent vire um contêiner flex de
          // altura travada (com overflow-hidden nele, não overflow-y-auto)
          // pra que só o corpo do step role internamente, mantendo
          // cabeçalho/stepper/rodapé fixos — sem isso o scroll não
          // funciona direito sobre os campos (só na barra do Dialog
          // inteiro). A tela final e o aviso de "múltiplos passbooks" não
          // usam Wizard, então mantêm o comportamento padrão do Dialog.
          canClone && !(finished && result) && 'flex max-h-[calc(100dvh-2rem)] flex-col overflow-hidden',
        )}
      >
        <DialogHeader>
          <DialogTitle>Clonar cliente</DialogTitle>
          <DialogDescription>
            {client ? `Cria um novo cliente a partir de "${client.name}", com um passbook próprio.` : null}
          </DialogDescription>
        </DialogHeader>

        {!canClone ? (
          <p className="text-sm text-destructive">
            Este cliente tem {passbookCount === 0 ? 'nenhum passbook' : `${passbookCount} passbooks`} — a
            clonagem só é suportada para clientes com exatamente 1 passbook.
          </p>
        ) : finished && result ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Guarde a senha abaixo — ela não será exibida novamente.
            </p>
            <Field label="E-mail de acesso">
              <Input readOnly value={result.user.email} />
            </Field>
            <Field label="Senha gerada">
              <SecretInput readOnly value={result.user.generatedPassword} />
            </Field>
            <Field label="Fixe.Club">
              <Input readOnly value={result.passbook.shortUrl} />
            </Field>
            <Field label="ID do passbook gerado">
              <CopyableField value={result.passbook._id} label="ID do passbook">
                <Input readOnly value={result.passbook._id} />
              </CopyableField>
            </Field>
            <p className="text-sm text-muted-foreground">{storesCreated} loja(s) cadastrada(s).</p>
            {result.warnings.length > 0 ? (
              <ul className="list-disc space-y-1 pl-4 text-xs text-warning">
                {result.warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            ) : null}
            <DialogFooter>
              <Button
                onClick={() => {
                  onOpenChange(false);
                  reset();
                }}
              >
                Concluir
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <Wizard
            steps={steps}
            onFinish={handleFinish}
            isSubmitting={submittingFinish}
            finishLabel="Concluir clonagem"
            scrollableBody
            className="min-h-0 flex-1"
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export function Field({ label, error, children }: { label: string; error?: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
